import {
  ViewIcon,
  CopyIcon,
  RepeatIcon,
  LockIcon,
  TimeIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  FormControl,
  Input,
  useToast,
  Box,
  Text,
  IconButton,
  Spinner,
  Switch,
  Avatar,
  Tooltip,
} from "@chakra-ui/react";
import axios from "axios";
import { useState } from "react";
import { ChatState } from "../../Context/ChatProvider";
import UserListItem from "../userAvatar/UserListItem";
import { Ornament, CrownIcon, TrashIcon } from "../ui/Icons";
import { copyText } from "../ui/clipboard";

const UpdateGroupChatModal = ({
  fetchMessages,
  fetchAgain,
  setFetchAgain,
  onDeleted,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const actionsDisc = useDisclosure();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [groupChatName, setGroupChatName] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tagSaving, setTagSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [loading, setLoading] = useState(false);
  const [renameloading, setRenameLoading] = useState(false);
  const [actions, setActions] = useState(null);
  const toast = useToast();

  const { selectedChat, setSelectedChat, user } = ChatState();

  const config = { headers: { Authorization: `Bearer ${user.token}` } };

  const ownerId = selectedChat.groupAdmin?._id;
  const adminIds = selectedChat.groupAdmins || [];
  const isOwner = ownerId === user._id;
  const iAmManager = isOwner || adminIds.includes(user._id);
  const mayInvite = iAmManager || selectedChat.membersCanInvite !== false;

  const roleOf = (id) =>
    id === ownerId ? "создатель" : adminIds.includes(id) ? "админ" : null;

  const inviteKey = selectedChat.inviteTag || selectedChat.inviteCode;
  const inviteLink = inviteKey
    ? `${window.location.origin}/join/${inviteKey}`
    : null;

  const oops = (error, title = "Не получилось") =>
    toast({
      title,
      description: error.response?.data?.message,
      status: "error",
      duration: 4000,
      isClosable: true,
      position: "bottom",
    });

  const copyLink = () => {
    copyText(inviteLink).then(() =>
      toast({
        title: "Ссылка скопирована",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "bottom",
      })
    );
  };

  const saveTag = async () => {
    try {
      setTagSaving(true);
      const { data } = await axios.put(
        "/api/chat/invite-tag",
        { chatId: selectedChat._id, tag: tagInput },
        config
      );
      setSelectedChat({ ...selectedChat, inviteTag: data.inviteTag });
      setTagInput("");
      toast({
        title: data.inviteTag ? `Тег @${data.inviteTag} сохранён` : "Тег убран",
        status: "success",
        duration: 2500,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      oops(error);
    } finally {
      setTagSaving(false);
    }
  };

  const regenerateLink = async () => {
    try {
      const { data } = await axios.post(
        "/api/chat/invite-code",
        { chatId: selectedChat._id },
        config
      );
      setSelectedChat({ ...selectedChat, inviteCode: data.inviteCode });
      toast({
        title: "Новая ссылка выпущена, старая больше не работает",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      oops(error);
    }
  };

  const handleSearch = async (query) => {
    setSearch(query);
    if (!query) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`/api/user?search=${query}`, config);
      setLoading(false);
      setSearchResult(data);
    } catch (error) {
      oops(error, "Не удалось выполнить поиск");
      setLoading(false);
    }
  };

  const handleRename = async () => {
    if (!groupChatName) return;
    try {
      setRenameLoading(true);
      const { data } = await axios.put(
        `/api/chat/rename`,
        { chatId: selectedChat._id, chatName: groupChatName },
        config
      );
      setSelectedChat(data);
      setFetchAgain(!fetchAgain);
      setRenameLoading(false);
    } catch (error) {
      oops(error);
      setRenameLoading(false);
    }
    setGroupChatName("");
  };

  const handleAddUser = async (user1) => {
    if (selectedChat.users.find((u) => u._id === user1._id)) {
      toast({
        title: "Этот художник уже в беседе",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.put(
        `/api/chat/groupadd`,
        { chatId: selectedChat._id, userId: user1._id },
        config
      );
      setSelectedChat(data);
      setFetchAgain(!fetchAgain);
      setLoading(false);
      setSearchResult([]);
      setSearch("");
    } catch (error) {
      oops(error);
      setLoading(false);
    }
  };

  const handleRemove = async (user1) => {
    try {
      setLoading(true);
      const { data } = await axios.put(
        `/api/chat/groupremove`,
        { chatId: selectedChat._id, userId: user1._id },
        config
      );

      user1._id === user._id ? setSelectedChat() : setSelectedChat(data);
      setFetchAgain(!fetchAgain);
      fetchMessages();
      setLoading(false);
    } catch (error) {
      oops(error);
      setLoading(false);
    }
  };

  const toggleAdmin = async (user1) => {
    try {
      const { data } = await axios.put(
        "/api/chat/group-admin",
        { chatId: selectedChat._id, userId: user1._id },
        config
      );
      setSelectedChat(data);
      setFetchAgain(!fetchAgain);
    } catch (error) {
      oops(error);
    }
  };

  const setPermission = async (field, value) => {
    try {
      const { data } = await axios.put(
        "/api/chat/permissions",
        { chatId: selectedChat._id, [field]: value },
        config
      );
      setSelectedChat(data);
      setFetchAgain(!fetchAgain);
    } catch (error) {
      oops(error);
    }
  };

  // беседу целиком может снести создатель (или админ клуба)
  const canDeleteChat = isOwner || user.isAdmin;

  const removeChat = async () => {
    try {
      setDeleting(true);
      const participants = selectedChat.users;
      await axios.delete(`/api/chat/${selectedChat._id}`, config);
      toast({
        title: `Беседа «${selectedChat.chatName}» удалена`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      setConfirmDelete(false);
      onClose();
      if (onDeleted) onDeleted(participants);
      setFetchAgain(!fetchAgain);
    } catch (error) {
      oops(error, "Не удалось удалить беседу");
    } finally {
      setDeleting(false);
    }
  };

  const openActions = async () => {
    actionsDisc.onOpen();
    setActions(null);
    try {
      const { data } = await axios.get(
        `/api/chat/${selectedChat._id}/actions`,
        config
      );
      setActions(data);
    } catch (error) {
      oops(error, "Журнал недоступен");
      setActions([]);
    }
  };

  const canKick = (u) => {
    if (u._id === user._id) return false; // свой выход — кнопкой внизу
    if (isOwner) return true;
    return iAmManager && !roleOf(u._id); // админ выгоняет только обычных
  };

  const blockStyle = {
    w: "100%",
    bg: "#F6E8C6",
    border: "1px solid #E4D2A6",
    borderRadius: "12px",
    px: 3,
    py: 2.5,
    mt: 2,
  };

  return (
    <>
      <IconButton
        d={{ base: "flex" }}
        icon={<ViewIcon />}
        onClick={onOpen}
        borderRadius="12px"
        bg="#F6E8C6"
        color="#243D27"
        _hover={{ bg: "#EAD9AC" }}
      />

      <Modal
        onClose={onClose}
        isOpen={isOpen}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="rgba(20, 35, 23, 0.45)" />
        <ModalContent maxH="85vh">
          <ModalHeader
            fontSize="26px"
            fontFamily="'Prata', serif"
            color="#243D27"
            d="flex"
            flexDir="column"
            alignItems="center"
            pb={2}
          >
            {selectedChat.chatName}
            <Text fontSize="12px" fontFamily="'Golos Text', sans-serif" color="rgba(36,61,39,0.55)" mt={1}>
              {selectedChat.isPrivate ? "закрытая беседа" : "открытая беседа"}
            </Text>
            <Box mt={2}>
              <Ornament color="#8D414B" opacity={0.55} width={110} />
            </Box>
          </ModalHeader>

          <ModalCloseButton borderRadius="10px" />
          <ModalBody d="flex" flexDir="column" pb={2}>
            {/* Участники с ролями */}
            <Text fontSize="sm" fontWeight={600} color="#243D27" mb={1}>
              Участники
            </Text>
            <Box maxH="180px" overflowY="auto" pr={1}>
              {selectedChat.users.map((u) => (
                <Box
                  key={u._id}
                  d="flex"
                  alignItems="center"
                  py={1.5}
                  px={2}
                  borderRadius="10px"
                  _hover={{ bg: "#F6E8C6" }}
                >
                  <Avatar size="xs" name={u.name} src={u.pic} mr={2} />
                  <Text fontSize="sm" flex="1" isTruncated>
                    {u.name}
                    {u._id === user._id && (
                      <Text as="span" fontSize="11px" color="#8D414B" ml={1}>
                        (вы)
                      </Text>
                    )}
                  </Text>
                  {roleOf(u._id) && (
                    <Box
                      d="inline-flex"
                      alignItems="center"
                      bg={u._id === ownerId ? "#8D414B" : "#243D27"}
                      color="#F2DFB6"
                      fontSize="10px"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      mr={1}
                    >
                      <CrownIcon size={9} />
                      <Text ml={1}>{roleOf(u._id)}</Text>
                    </Box>
                  )}
                  {isOwner && u._id !== ownerId && (
                    <Tooltip
                      label={
                        adminIds.includes(u._id)
                          ? "Снять админа"
                          : "Назначить админом"
                      }
                      hasArrow
                    >
                      <IconButton
                        aria-label="Права"
                        icon={<CrownIcon size={13} />}
                        size="xs"
                        variant="ghost"
                        color={
                          adminIds.includes(u._id)
                            ? "#8D414B"
                            : "rgba(36,61,39,0.4)"
                        }
                        onClick={() => toggleAdmin(u)}
                      />
                    </Tooltip>
                  )}
                  {canKick(u) && (
                    <Tooltip label="Исключить" hasArrow>
                      <IconButton
                        aria-label="Исключить"
                        icon={<CloseIcon boxSize="8px" />}
                        size="xs"
                        variant="ghost"
                        colorScheme="burgundy"
                        onClick={() => handleRemove(u)}
                      />
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>

            {/* Переименование */}
            {iAmManager && (
              <FormControl d="flex" mt={3}>
                <Input
                  placeholder="Новое название беседы"
                  size="sm"
                  borderRadius="10px"
                  value={groupChatName}
                  onChange={(e) => setGroupChatName(e.target.value)}
                />
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="pine"
                  ml={2}
                  borderRadius="10px"
                  isLoading={renameloading}
                  onClick={handleRename}
                >
                  Сохранить
                </Button>
              </FormControl>
            )}

            {/* Добавление участников */}
            {mayInvite && (
              <FormControl mt={2}>
                <Input
                  placeholder="Добавить участника"
                  size="sm"
                  borderRadius="10px"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </FormControl>
            )}
            {loading ? (
              <Spinner size="sm" color="#8D414B" mt={2} alignSelf="center" />
            ) : (
              search &&
              searchResult
                ?.slice(0, 4)
                .map((u) => (
                  <UserListItem
                    key={u._id}
                    user={u}
                    handleFunction={() => handleAddUser(u)}
                  />
                ))
            )}

            {/* Разрешения */}
            {iAmManager && (
              <Box {...blockStyle}>
                <Text fontSize="sm" fontWeight={600} color="#243D27" mb={1}>
                  Разрешения
                </Text>
                <Box d="flex" alignItems="center" justifyContent="space-between" py={1}>
                  <Text fontSize="13px">Писать могут все</Text>
                  <Switch
                    size="sm"
                    colorScheme="burgundy"
                    isChecked={selectedChat.membersCanWrite !== false}
                    onChange={(e) =>
                      setPermission("membersCanWrite", e.target.checked)
                    }
                  />
                </Box>
                <Box d="flex" alignItems="center" justifyContent="space-between" py={1}>
                  <Text fontSize="13px">Приглашать могут все</Text>
                  <Switch
                    size="sm"
                    colorScheme="burgundy"
                    isChecked={selectedChat.membersCanInvite !== false}
                    onChange={(e) =>
                      setPermission("membersCanInvite", e.target.checked)
                    }
                  />
                </Box>
              </Box>
            )}

            {/* Ссылка-приглашение */}
            {inviteLink && (!selectedChat.isPrivate || iAmManager) && (
              <Box {...blockStyle}>
                <Box d="flex" alignItems="center" mb={1.5}>
                  {selectedChat.isPrivate && (
                    <LockIcon boxSize="11px" mr={1.5} color="#8D414B" />
                  )}
                  <Text fontSize="sm" fontWeight={600} color="#243D27">
                    Ссылка-приглашение
                  </Text>
                  {iAmManager && (
                    <Tooltip label="Перевыпустить ссылку" hasArrow>
                      <IconButton
                        aria-label="Новая ссылка"
                        icon={<RepeatIcon boxSize="12px" />}
                        size="xs"
                        variant="ghost"
                        colorScheme="burgundy"
                        ml="auto"
                        onClick={regenerateLink}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Box d="flex">
                  <Input
                    size="sm"
                    borderRadius="8px"
                    isReadOnly
                    value={inviteLink}
                    fontSize="12px"
                  />
                  <IconButton
                    aria-label="Скопировать"
                    icon={<CopyIcon />}
                    size="sm"
                    ml={2}
                    borderRadius="8px"
                    bg="#243D27"
                    color="#F2DFB6"
                    _hover={{ bg: "#2E4A33" }}
                    onClick={copyLink}
                  />
                </Box>
                {iAmManager && !selectedChat.isPrivate && (
                  <Box d="flex" mt={2}>
                    <Input
                      size="sm"
                      borderRadius="8px"
                      placeholder={
                        selectedChat.inviteTag
                          ? `сейчас: @${selectedChat.inviteTag}`
                          : "свой_тег (латиница, цифры, _)"
                      }
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                    />
                    <Button
                      size="sm"
                      ml={2}
                      borderRadius="8px"
                      variant="outline"
                      colorScheme="pine"
                      isLoading={tagSaving}
                      onClick={saveTag}
                    >
                      Тег
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </ModalBody>

          <ModalFooter d="flex" flexDir="column" alignItems="stretch" pt={2}>
            <Box d="flex" justifyContent="space-between" alignItems="center">
              {iAmManager ? (
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="pine"
                  leftIcon={<TimeIcon boxSize="12px" />}
                  onClick={openActions}
                >
                  Недавние действия
                </Button>
              ) : (
                <span />
              )}
              <Button
                size="sm"
                onClick={() => handleRemove(user)}
                variant="outline"
                colorScheme="burgundy"
                _hover={{ bg: "#8D414B", color: "#FBF0D9" }}
              >
                Покинуть беседу
              </Button>
            </Box>
            {canDeleteChat && (
              <Button
                size="sm"
                mt={2}
                w="100%"
                variant="ghost"
                colorScheme="burgundy"
                leftIcon={<TrashIcon size={13} />}
                onClick={() => setConfirmDelete(true)}
              >
                Удалить беседу для всех
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Подтверждение удаления беседы */}
      <Modal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        isCentered
        size="sm"
      >
        <ModalOverlay bg="rgba(20, 35, 23, 0.55)" />
        <ModalContent>
          <ModalHeader
            fontSize="20px"
            fontFamily="'Prata', serif"
            color="#243D27"
            pb={1}
          >
            Удалить беседу?
          </ModalHeader>
          <ModalBody py={2}>
            <Text
              fontSize="sm"
              fontWeight={600}
              color="#243D27"
              bg="#F6E8C6"
              borderRadius="10px"
              px={3}
              py={2}
              isTruncated
            >
              {selectedChat.chatName}
            </Text>
            <Text fontSize="xs" opacity={0.6} mt={2}>
              Беседа исчезнет у всех участников вместе со всей перепиской и
              вложениями. Это действие нельзя отменить.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="pine"
              mr={2}
              onClick={() => setConfirmDelete(false)}
            >
              Отмена
            </Button>
            <Button
              size="sm"
              colorScheme="burgundy"
              isLoading={deleting}
              onClick={removeChat}
              _hover={{ bg: "#7A3841" }}
            >
              Удалить
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Журнал «Недавние действия» */}
      <Modal
        onClose={actionsDisc.onClose}
        isOpen={actionsDisc.isOpen}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="rgba(20, 35, 23, 0.45)" />
        <ModalContent maxH="70vh">
          <ModalHeader
            fontSize="22px"
            fontFamily="'Prata', serif"
            color="#243D27"
            d="flex"
            flexDir="column"
            alignItems="center"
            pb={2}
          >
            Недавние действия
            <Box mt={2}>
              <Ornament color="#8D414B" opacity={0.55} width={110} />
            </Box>
          </ModalHeader>
          <ModalCloseButton borderRadius="10px" />
          <ModalBody pb={5}>
            {!actions ? (
              <Spinner color="#8D414B" size="lg" d="block" mx="auto" my={8} />
            ) : actions.length === 0 ? (
              <Text textAlign="center" fontSize="sm" opacity={0.6} py={6}>
                Пока тихо — действий не было
              </Text>
            ) : (
              actions.map((a) => (
                <Box
                  key={a._id}
                  d="flex"
                  alignItems="center"
                  py={2}
                  borderBottom="1px solid #F0E2BE"
                >
                  <Avatar
                    size="xs"
                    name={a.actor?.name}
                    src={a.actor?.pic}
                    mr={2}
                  />
                  <Box flex="1" minW={0}>
                    <Text fontSize="sm">
                      <b>{a.actor?.name || "Кто-то"}</b> {a.text}
                    </Text>
                    <Text fontSize="11px" opacity={0.6}>
                      {new Date(a.createdAt).toLocaleString("ru-RU", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Text>
                  </Box>
                </Box>
              ))
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UpdateGroupChatModal;
